import React, { useEffect, useState, useCallback } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { Target, Coins, Zap, Check } from "lucide-react";
import { Sound } from "../game/audio";

export default function Missions({ onClose }) {
  const { applyUser } = usePlayer();
  const [missions, setMissions] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => api.missions().then(({ data }) => setMissions(data.missions)).catch((e) => setErr(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    setBusy(id); setErr(null);
    try { const { data } = await api.claimMission(id); applyUser(data.user); Sound.levelup(); await load(); }
    catch (e) { setErr(apiErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <Panel title="Missions" onClose={onClose}>
      <div className="text-slate-400 text-sm mb-3">Complete missions across matches. Progress is server-validated.</div>
      {err && <div className="text-red-400 text-xs mb-2">{err}</div>}
      <div className="space-y-2">
        {missions.map((m) => {
          const pct = Math.min(100, (m.progress / m.target) * 100);
          const done = m.progress >= m.target;
          return (
            <div key={m.id} className="card p-3" data-testid={`mission-${m.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Target size={16} className="text-cyan-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white truncate">{m.text}</div>
                    <div className="text-[11px] text-slate-400">{Math.min(m.progress, m.target).toLocaleString()} / {m.target.toLocaleString()}</div>
                  </div>
                </div>
                {m.claimed ? (
                  <span className="text-green-400 text-xs font-bold flex items-center gap-1"><Check size={14} /> Claimed</span>
                ) : (
                  <button disabled={!done || busy === m.id} onClick={() => claim(m.id)}
                    className={`text-xs py-1.5 px-3 rounded-lg font-bold ${done ? "btn-primary" : "bg-slate-800 text-slate-500"}`} data-testid={`claim-mission-${m.id}`}>
                    Claim
                  </button>
                )}
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-400" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex gap-3 mt-2 text-[11px]">
                {m.reward.coins ? <span className="flex items-center gap-1 text-amber-400"><Coins size={11} /> {m.reward.coins}</span> : null}
                {m.reward.xp ? <span className="flex items-center gap-1 text-cyan-400"><Zap size={11} /> {m.reward.xp} XP</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
